import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ProgramTypesGUID } from '../models/_enum-collections';
import { ApiResponse } from '../models/api-response.model';
import {
  EventCountModel,
  PendingReviewCountModel
} from '../models/event-count-model';
import { EventCustomerDetails } from '../models/event-customer-details.model';
import { EventMilestone2PaymentDetails } from '../models/event-milestone-2-payment-details.model';
import {
  EventNoteModel,
  AddEventNoteRequestModel
} from '../models/event-note.model';
import { EventPartnerModel } from '../models/event-partner.model';
import {
  EventPaymentInformationModel,
  UpdateEventPaymentInformationRequest
} from '../models/event-payment-information.model';
import {
  EventRegistrationModel,
  EventRegistrationCustomerModel
} from '../models/event-registration.model';
import { EventSearchFilterRequest } from '../models/event-search-filter-request.model';
import { EventTargetProduct } from '../models/event-target-product.model';
import { Milestone2InvoiceRequest } from '../models/milestone2-invoice-request.model';
import { Milestone2PaymentStatus } from '../models/milestone2-payment-status.model';
import { PagedResult } from '../models/page-result.model';
import { PartnerComplianceInfoEventAllocationData } from '../models/partner-compliance-info-event-allocation-data.model';
import { PayoutMetricOptions } from '../models/payout-metric-options.model';
import { PCInfoEventAllocationRequest } from '../models/pc-info-event-allocation-request.model';
import { PoeDocumentModel } from '../models/poe-document.model';
import { EventsApiBase } from './apiBase';
import { EventType } from '../models/event-type.model';

@Injectable({
  providedIn: 'root'
})
export class EventDashboardApi extends EventsApiBase {
  getEventRegistration(
    eventRegistrationId: number
  ): Observable<EventRegistrationModel> {
    return this.get<EventRegistrationModel>(
      `api/EventDashboard/EventRegistration/${eventRegistrationId}`
    );
  }

  getAllEvents(
    request: EventSearchFilterRequest
  ): Observable<PagedResult<EventRegistrationModel>> {
    return this.post<
      EventSearchFilterRequest,
      PagedResult<EventRegistrationModel>
    >(`api/EventDashboard/SearchAllEvents`, request);
  }

  getAwaitingCustomerValidationEvents(
    request: EventSearchFilterRequest
  ): Observable<PagedResult<EventRegistrationModel>> {
    return this.post<
      EventSearchFilterRequest,
      PagedResult<EventRegistrationModel>
    >(`api/EventDashboard/SearchAwaitingCustomerValidationEvents`, request);
  }

  getAwaitingInvoiceDocReviewEvents(
    request: EventSearchFilterRequest
  ): Observable<PagedResult<EventRegistrationModel>> {
    return this.post<
      EventSearchFilterRequest,
      PagedResult<EventRegistrationModel>
    >(`api/EventDashboard/SearchInvoiceDocReviewEvents`, request);
  }

  getAwaitingCustomerSurveyReviewEvents(
    request: EventSearchFilterRequest
  ): Observable<PagedResult<EventRegistrationModel>> {
    return this.post<
      EventSearchFilterRequest,
      PagedResult<EventRegistrationModel>
    >(`api/EventDashboard/SearchCustomerSurveyReviewEvents`, request);
  }

  getEventPartnerDetails(
    eventRegistrationId: number
  ): Observable<EventPartnerModel> {
    return this.get<EventPartnerModel>(
      `api/EventDetails/GetPartnerDetails/${eventRegistrationId}`
    );
  }

  updatePartnerDetails(request: EventPartnerModel): Observable<boolean> {
    return this.post<EventPartnerModel, boolean>(
      'api/EventDetails/UpdatePartnerDetails',
      request
    );
  }

  getEventCount(): Observable<EventCountModel> {
    return this.get<EventCountModel>('api/EventDashboard/EventsCount');
  }

  getEngagementsCount(
    programTypeGuid: ProgramTypesGUID
  ): Observable<PendingReviewCountModel> {
    return this.get<PendingReviewCountModel>(
      'api/EventDashboard/GetEngagementsCount/' + programTypeGuid
    );
  }

  getPendingReviewQueues(
    programTypeGuid: ProgramTypesGUID
  ): Observable<PendingReviewCountModel> {
    return this.get<PendingReviewCountModel>(
      'api/EventDashboard/GetPendingReviewQueues/' + programTypeGuid
    );
  }

  getEventCustomers(
    eventRegistrationId: number
  ): Observable<EventRegistrationCustomerModel[]> {
    return this.get<EventRegistrationCustomerModel[]>(
      `api/EventDetails/GetEventCustomers/${eventRegistrationId}`
    );
  }

  getOneToOneEventCustomerDetails(
    eventRegistrationId: number
  ): Observable<EventCustomerDetails> {
    return this.get<EventCustomerDetails>(
      `api/EventDetails/GetOneToOneEventCustomerDetails/${eventRegistrationId}`
    );
  }

  getEventPaymentInformation(
    eventId: number,
    programTypeGuid: string
  ): Observable<EventPaymentInformationModel> {
    return this.get<EventPaymentInformationModel>(
      `api/EventDashboard/PaymentInformation/${eventId}/${programTypeGuid}`
    );
  }

  updateEventPaymentInformation(
    request: UpdateEventPaymentInformationRequest
  ): Observable<ApiResponse> {
    return this.post<UpdateEventPaymentInformationRequest, ApiResponse>(
      `api/EventDashboard/UpdatePaymentInformation`,
      request
    );
  }
  getAwaitingSKUPurchaseReviewEvents(
    request: EventSearchFilterRequest
  ): Observable<PagedResult<EventRegistrationModel>> {
    return this.post<
      EventSearchFilterRequest,
      PagedResult<EventRegistrationModel>
    >(`api/EventDashboard/SearchSKUPurchaseReviewEvents`, request);
  }

  getAwaitingDAPoeReviewEvents(
    request: EventSearchFilterRequest
  ): Observable<PagedResult<EventRegistrationModel>> {
    return this.post<
      EventSearchFilterRequest,
      PagedResult<EventRegistrationModel>
    >(`api/EventDashboard/SearchDAPoeReviewEvents`, request);
  }

  getEventNotes(eventRegistrationId: number): Observable<EventNoteModel[]> {
    return this.get<EventNoteModel[]>(
      `api/EventNotes/eventRegistration/` + eventRegistrationId
    );
  }

  addEventNote(
    addEventNoteRequest: AddEventNoteRequestModel
  ): Observable<ApiResponse> {
    return this.post<AddEventNoteRequestModel, ApiResponse>(
      `api/EventNotes/AddEventNotes`,
      addEventNoteRequest
    );
  }

  getTargetProductsList(): Observable<EventTargetProduct[]> {
    return this.get<EventTargetProduct[]>(
      'api/EventDetails/GetEventTargetProducts'
    );
  }

  getMilestone2PaymentDetails(
    eventRegistrationId: number,
    programTypeGuId: string
  ): Observable<EventMilestone2PaymentDetails> {
    return this.get<EventMilestone2PaymentDetails>(
      `api/EventDetails/GetMilestone2PaymentDetails/${eventRegistrationId}/${programTypeGuId}`
    );
  }

  getMilestone2PaymentStatuses(): Observable<Milestone2PaymentStatus[]> {
    return this.get<Milestone2PaymentStatus[]>(
      `api/EventDetails/GetMilestone2PaymentStatuses`
    );
  }

  getPayoutMetricOptions(
    eventRegistrationId: number
  ): Observable<PayoutMetricOptions[]> {
    return this.get<PayoutMetricOptions[]>(
      `api/EventDetails/GetPayoutMetricOptions/${eventRegistrationId}`
    );
  }

  updateMilestone2Details(request: Milestone2InvoiceRequest) {
    return this.post<Milestone2InvoiceRequest, boolean>(
      'api/EventDetails/UpdateMilestone2Invoice',
      request
    );
  }

  getEventTypes(): Observable<EventType[]> {
    return this.get<EventType[]>('api/EventType/GetEventTypes');
  }

  getAllWorkshops(): Observable<EventType[]> {
    return this.get<EventType[]>('api/EventType/GetAllWorkshops');
  }

  getPartnerComplianceAllocationData(
    request: PCInfoEventAllocationRequest
  ): Observable<PartnerComplianceInfoEventAllocationData> {
    return this.post<
      PCInfoEventAllocationRequest,
      PartnerComplianceInfoEventAllocationData
    >('api/EventDashboard/GetPartnerComplianceEventAllocationData', request);
  }

  getPoeDocuments(progrmaTypeGUID: string): Observable<PoeDocumentModel[]> {
    return this.get<PoeDocumentModel[]>(
      '/api/EventDashboard/GetPoeDocuments/' + progrmaTypeGUID
    );
  }

  getPOEDocReviewEngagements(
    request: EventSearchFilterRequest
  ): Observable<PagedResult<EventRegistrationModel>> {
    return this.post<
      EventSearchFilterRequest,
      PagedResult<EventRegistrationModel>
    >(`api/EventDashboard/SearchPOEDocReviewEngagements`, request);
  }

  getEngagementsForMrppCustAckList(
    request: EventSearchFilterRequest
  ): Observable<PagedResult<EventRegistrationModel>> {
    return this.post<
      EventSearchFilterRequest,
      PagedResult<EventRegistrationModel>
    >('/api/EventDashboard/GetEngagementsForMrppCustAckList', request);
  }

  updateCustomerDetails(request: EventCustomerDetails): Observable<boolean> {
    return this.post<EventCustomerDetails, boolean>(
      'api/EventDetails/UpdateEventCustomerDetails',
      request
    );
  }

  getAwaitingCustomerSurveyReviewMrpp(
    request: EventSearchFilterRequest
  ): Observable<PagedResult<EventRegistrationModel>> {
    return this.post<
      EventSearchFilterRequest,
      PagedResult<EventRegistrationModel>
    >(`api/EventDashboard/SearchCustomerSurveyReviewMrpp`, request);
  }
}
